import React, { useEffect, useMemo, useRef, useState } from "react";
import { T, radii } from "./theme";

// ─────────────────────────────────────────────────────────────────────────
// HeaderRight context — lets OnboardingFlowV2 inject a sign-in link (or
// any other right-side header chrome) into every ScreenShell without
// plumbing a prop through every screen component.
// ─────────────────────────────────────────────────────────────────────────

const ScreenHeaderRightContext = React.createContext<React.ReactNode>(null);

export function ScreenHeaderRightProvider({
  value,
  children,
}: {
  value: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  return <ScreenHeaderRightContext.Provider value={value}>{children}</ScreenHeaderRightContext.Provider>;
}

// ─────────────────────────────────────────────────────────────────────────
// ScreenShell — full-bleed dark layout with optional progress bar + back +
// a fixed bottom CTA. iOS-first; works on web by sizing to viewport.
// ─────────────────────────────────────────────────────────────────────────

export type ScreenShellProps = {
  step: number;
  total: number;
  title: string;
  subtitle?: React.ReactNode;
  topPills?: React.ReactNode;
  onBack: (() => void) | null;
  primaryLabel: string;
  primaryDisabled?: boolean;
  onPrimary: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
  isMobile: boolean;
  children: React.ReactNode;
};

export function ScreenShell(props: ScreenShellProps): React.ReactElement {
  const {
    step,
    total,
    title,
    subtitle,
    topPills,
    onBack,
    primaryLabel,
    primaryDisabled,
    onPrimary,
    secondaryLabel,
    onSecondary,
    isMobile,
    children,
  } = props;

  const headerRight = React.useContext(ScreenHeaderRightContext);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: T.bgPage,
        color: T.fg,
        display: "flex",
        flexDirection: "column",
        zIndex: 250,
        fontFamily: "'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Arial",
      }}
    >
      <div
        style={{
          padding: isMobile ? "max(14px, env(safe-area-inset-top)) 20px 8px" : "18px 28px 8px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            style={{
              background: "transparent",
              border: "none",
              color: T.fg,
              padding: 4,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        ) : (
          <span style={{ width: 22 }} />
        )}
        <div style={{ flex: 1, minHeight: 4 }}>
          <StepBar current={step} total={total} />
        </div>
        {headerRight ? (
          <div style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}>{headerRight}</div>
        ) : (
          <span style={{ width: 22 }} />
        )}
      </div>

      {topPills ? (
        <div style={{ padding: isMobile ? "12px 20px 0" : "12px 28px 0" }}>{topPills}</div>
      ) : null}

      <div
        style={{
          flex: 1,
          padding: isMobile ? "16px 20px 24px" : "28px 28px 24px",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <h1
          style={{
            fontSize: isMobile ? "1.85rem" : "2.2rem",
            fontWeight: 700,
            letterSpacing: "-0.025em",
            lineHeight: 1.1,
            margin: 0,
          }}
        >
          {title}
        </h1>
        {subtitle ? (
          <p
            style={{
              color: T.fg2,
              fontSize: "0.96rem",
              lineHeight: 1.45,
              margin: "8px 0 0",
            }}
          >
            {subtitle}
          </p>
        ) : null}
        <div style={{ marginTop: 28 }}>{children}</div>
      </div>

      <div
        style={{
          padding: isMobile
            ? "12px 20px max(28px, env(safe-area-inset-bottom))"
            : "16px 28px 24px",
          borderTop: `1px solid ${T.hairline}`,
          background: T.bgPage,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <Btn full size="xl" kind="primary" onClick={onPrimary} disabled={primaryDisabled}>
          {primaryLabel}
        </Btn>
        {secondaryLabel && onSecondary ? (
          <Btn full size="md" kind="ghost" onClick={onSecondary}>
            {secondaryLabel}
          </Btn>
        ) : null}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// StepBar — thin segmented progress (one slot per step).
// ─────────────────────────────────────────────────────────────────────────

export function StepBar({ current, total }: { current: number; total: number }): React.ReactElement {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            height: 3,
            borderRadius: 2,
            background: i < current ? T.fg : "rgba(255,255,255,0.18)",
            transition: `background ${T.motion}`,
          }}
        />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Btn — primary / secondary / ghost / white.
// ─────────────────────────────────────────────────────────────────────────

export type BtnKind = "primary" | "secondary" | "ghost" | "white" | "danger";
export type BtnSize = "sm" | "md" | "lg" | "xl";

export type BtnProps = {
  kind?: BtnKind;
  size?: BtnSize;
  full?: boolean;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
  style?: React.CSSProperties;
  type?: "button" | "submit";
};

export function Btn({
  kind = "primary",
  size = "lg",
  full,
  icon,
  iconRight,
  disabled,
  onClick,
  children,
  style,
  type = "button",
}: BtnProps): React.ReactElement {
  const sizes = {
    xl: { h: 56, fz: "1.0rem", pad: "0 24px", radius: radii.lg, gap: 10 },
    lg: { h: 52, fz: "0.96rem", pad: "0 22px", radius: radii.lg, gap: 10 },
    md: { h: 44, fz: "0.9rem", pad: "0 18px", radius: radii.md, gap: 8 },
    sm: { h: 36, fz: "0.84rem", pad: "0 14px", radius: 8, gap: 6 },
  }[size];
  const kinds: Record<BtnKind, React.CSSProperties> = {
    primary: {
      background: T.ctaGrad,
      color: "#0a0e14",
      border: "1px solid rgba(255,255,255,0.18)",
      fontWeight: 800,
    },
    white: {
      background: "#fff",
      color: "#0a0e14",
      border: "none",
      fontWeight: 700,
    },
    secondary: {
      background: "rgba(255,255,255,0.04)",
      color: T.fg,
      border: `1px solid ${T.hairlineStrong}`,
      fontWeight: 700,
    },
    ghost: {
      background: "transparent",
      color: T.fg2,
      border: "1px solid transparent",
      fontWeight: 700,
    },
    danger: {
      background: "rgba(239,68,68,0.12)",
      color: "#fca5a5",
      border: "1px solid rgba(239,68,68,0.32)",
      fontWeight: 700,
    },
  };
  return (
    <button
      type={type}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: sizes.gap,
        height: sizes.h,
        padding: sizes.pad,
        width: full ? "100%" : "auto",
        borderRadius: sizes.radius,
        fontSize: sizes.fz,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        transition: `all ${T.motion}`,
        letterSpacing: "-0.005em",
        ...kinds[kind],
        ...style,
      }}
    >
      {icon}
      {children}
      {iconRight}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SegmentedToggle (lb/kg, ft/cm).
// ─────────────────────────────────────────────────────────────────────────

export function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  size = "md",
}: {
  options: { value: T; label: string }[];
  /** `null` / `undefined` = no selection — no option is highlighted. */
  value: T | null | undefined;
  onChange: (v: T) => void;
  size?: "sm" | "md";
}): React.ReactElement {
  const h = size === "sm" ? 32 : 38;
  const fz = size === "sm" ? "0.78rem" : "0.86rem";
  return (
    <div
      role="tablist"
      style={{
        display: "inline-flex",
        height: h,
        background: "rgba(255,255,255,0.04)",
        border: `1px solid ${T.hairlineStrong}`,
        borderRadius: 999,
        padding: 3,
        gap: 2,
      }}
    >
      {options.map((o) => {
        const active = value != null && o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            style={{
              padding: "0 16px",
              borderRadius: 999,
              border: "none",
              background: active ? "#fff" : "transparent",
              color: active ? "#0a0e14" : T.fg2,
              fontWeight: 700,
              fontSize: fz,
              cursor: "pointer",
              transition: `all ${T.motion}`,
              letterSpacing: "-0.005em",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// LivePill — the recalculating "X kcal initial daily budget" / "end date" pair.
// ─────────────────────────────────────────────────────────────────────────

export function LivePillRow({
  primary,
  primaryLabel,
  secondary,
  secondaryLabel,
}: {
  primary: string;
  primaryLabel: string;
  secondary: string;
  secondaryLabel: string;
}): React.ReactElement {
  const cell: React.CSSProperties = {
    flex: 1,
    padding: "14px 16px",
    borderRadius: 14,
    border: `1px solid ${T.hairline}`,
    background: "rgba(255,255,255,0.025)",
    minWidth: 0,
  };
  return (
    <div style={{ display: "flex", gap: 10 }}>
      <div style={{ ...cell, background: "rgba(132,204,22,0.08)", borderColor: "rgba(132,204,22,0.32)" }}>
        <div style={{ fontSize: "1.4rem", fontWeight: 800, lineHeight: 1.1, ...T.tabular, letterSpacing: "-0.02em" }}>
          {primary}
        </div>
        <div style={{ marginTop: 4, color: T.fg2, fontWeight: 600, fontSize: "0.78rem" }}>{primaryLabel}</div>
      </div>
      <div style={cell}>
        <div style={{ fontSize: "1.4rem", fontWeight: 800, lineHeight: 1.1, ...T.tabular, letterSpacing: "-0.02em" }}>
          {secondary}
        </div>
        <div style={{ marginTop: 4, color: T.fg2, fontWeight: 600, fontSize: "0.78rem" }}>{secondaryLabel}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// RulerInput — horizontal tick ruler with center marker.
// Drag/swipe horizontally to scrub the value. Uses pointer events so it
// works on touch and mouse.
// ─────────────────────────────────────────────────────────────────────────

export type RulerInputProps = {
  value: number;
  min: number;
  max: number;
  step: number;
  majorStep: number;
  unit: string;
  onChange: (next: number) => void;
  decimals?: number;
  accent?: "lime" | "blue";
  /** Hide the built-in big-number display (caller provides its own header). */
  hideValue?: boolean;
  /** Smaller number + tighter track. Useful when stacking multiple rulers
   * (e.g. the desktop target step) to fit on a normal-sized screen. */
  compact?: boolean;
  /** When set, the ruler is centered on this value instead of `value`. The
   * indicator slides off-center to show how far `value` sits from the anchor.
   * Switches drag interaction from pan-the-track to drag-the-indicator. */
  anchorValue?: number;
  /** Optional shaded band drawn between two values, highlighting the delta.
   * Only renders when `anchorValue` is set. Gradient brightens toward `to`. */
  highlightRange?: { from: number; to: number; tone: "loss" | "gain" };
};

export function RulerInput({
  value,
  min,
  max,
  step,
  majorStep,
  unit,
  onChange,
  decimals = 0,
  accent = "lime",
  hideValue = false,
  compact = false,
  anchorValue,
  highlightRange,
}: RulerInputProps): React.ReactElement {
  const anchored = anchorValue !== undefined;
  const center = anchored ? anchorValue : value;
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(320);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const measure = () => setWidth(el.offsetWidth || 320);
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // Fixed visual spacing between major labels — keeps labels readable
  // regardless of value range (was: pxPerUnit = width / (max - min), which
  // crammed 16 labels into ~350 px for a typical weight ruler).
  const pxPerUnit = useMemo(() => {
    const PX_PER_MAJOR = 64;
    return PX_PER_MAJOR / Math.max(1, majorStep);
  }, [majorStep]);

  const ticks = useMemo(() => {
    const out: { v: number; major: boolean }[] = [];
    const stepCount = Math.round((max - min) / step);
    const stepsPerMajor = Math.max(1, Math.round(majorStep / step));
    for (let i = 0; i <= stepCount; i += 1) {
      const v = +(min + i * step).toFixed(2);
      const major = i % stepsPerMajor === 0;
      out.push({ v, major });
    }
    return out;
  }, [min, max, step, majorStep]);

  // pointer-drag scrubbing. Two modes:
  //  - Free (default): drag pans the track; center always shows `value`.
  //  - Anchored: center is fixed at `anchorValue`; pointer x maps directly to
  //    a value (drag-the-indicator). Tapping anywhere on the track jumps the
  //    indicator there, which feels more discoverable than scroll-to-scrub.
  const dragRef = useRef<{ startX: number; startValue: number } | null>(null);
  function valueAtClientX(clientX: number): number {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return value;
    const localX = clientX - rect.left;
    const raw = center + (localX - width / 2) / pxPerUnit;
    const rounded = Math.round(raw / step) * step;
    return Math.min(max, Math.max(min, rounded));
  }
  function onPointerDown(e: React.PointerEvent<HTMLDivElement>): void {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    if (anchored) {
      const next = valueAtClientX(e.clientX);
      dragRef.current = { startX: e.clientX, startValue: next };
      if (next !== value) onChange(+next.toFixed(decimals + 2));
    } else {
      dragRef.current = { startX: e.clientX, startValue: value };
    }
  }
  function onPointerMove(e: React.PointerEvent<HTMLDivElement>): void {
    const d = dragRef.current;
    if (!d) return;
    let clamped: number;
    if (anchored) {
      clamped = valueAtClientX(e.clientX);
    } else {
      const dx = e.clientX - d.startX;
      const next = d.startValue - dx / pxPerUnit;
      const rounded = Math.round(next / step) * step;
      clamped = Math.min(max, Math.max(min, rounded));
    }
    if (clamped !== value) onChange(+clamped.toFixed(decimals + 2));
  }
  function onPointerUp(e: React.PointerEvent<HTMLDivElement>): void {
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    dragRef.current = null;
  }

  // Render ticks relative to `center` — equals `value` in free mode, equals
  // `anchorValue` in anchored mode (so the indicator slides off-center).
  const half = width / 2;
  const accentColor = accent === "lime" ? T.lime : T.blue;
  const displayed = value.toFixed(decimals);
  const indicatorX = anchored ? half + (value - center) * pxPerUnit : half;

  // Highlight band between two values (e.g. current → target weight). The
  // gradient brightens toward `to` so the eye follows the direction of change.
  let highlight: { left: number; width: number; gradient: string } | null = null;
  if (anchored && highlightRange) {
    const { from, to, tone } = highlightRange;
    const xFrom = half + (from - center) * pxPerUnit;
    const xTo = half + (to - center) * pxPerUnit;
    const left = Math.min(xFrom, xTo);
    const right = Math.max(xFrom, xTo);
    const w = right - left;
    if (w > 1) {
      const dir = to >= from ? "to right" : "to left";
      const stops =
        tone === "loss"
          ? "rgba(132,204,22,0.08), rgba(132,204,22,0.42)"
          : "rgba(255,255,255,0.06), rgba(255,255,255,0.22)";
      highlight = { left, width: w, gradient: `linear-gradient(${dir}, ${stops})` };
    }
  }

  return (
    <div style={{ userSelect: "none", touchAction: "pan-y" }}>
      {hideValue ? null : (
        <div
          style={{
            textAlign: "center",
            fontSize: compact ? "2.0rem" : "2.8rem",
            fontWeight: 700,
            letterSpacing: "-0.035em",
            color: T.fg,
            ...T.tabular,
            lineHeight: 1,
          }}
        >
          {displayed}
          <span style={{ fontSize: compact ? "0.9rem" : "1.1rem", color: T.muted, fontWeight: 700, marginLeft: 8, letterSpacing: 0 }}>
            {unit}
          </span>
        </div>
      )}
      <div
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{
          marginTop: hideValue ? 0 : compact ? 12 : 22,
          height: compact ? 52 : 70,
          position: "relative",
          overflow: "hidden",
          cursor: anchored ? "pointer" : "grab",
        }}
      >
        {/* highlight band (anchored mode only) — sits behind ticks, aligned
            with the indicator's vertical extent so it reads as a trail */}
        {highlight ? (
          <div
            aria-hidden
            style={{
              position: "absolute",
              left: highlight.left,
              width: highlight.width,
              top: compact ? 16 : 22,
              height: compact ? 30 : 40,
              background: highlight.gradient,
              borderRadius: 4,
              pointerEvents: "none",
            }}
          />
        ) : null}
        {/* numeric markers above ticks */}
        <div style={{ position: "absolute", inset: 0 }}>
          {ticks.filter((t) => t.major).map((t) => {
            const x = half + (t.v - center) * pxPerUnit;
            if (x < -40 || x > width + 40) return null;
            return (
              <div
                key={t.v}
                style={{
                  position: "absolute",
                  left: x,
                  top: 0,
                  transform: "translateX(-50%)",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: T.muted,
                  ...T.tabular,
                }}
              >
                {t.v}
              </div>
            );
          })}
        </div>
        {/* tick row */}
        <div style={{ position: "absolute", top: compact ? 18 : 24, left: 0, right: 0, bottom: 0 }}>
          {ticks.map((t) => {
            const x = half + (t.v - center) * pxPerUnit;
            if (x < -10 || x > width + 10) return null;
            return (
              <div
                key={t.v}
                style={{
                  position: "absolute",
                  left: x,
                  top: t.major ? (compact ? 4 : 6) : (compact ? 12 : 18),
                  width: 1.5,
                  height: t.major ? (compact ? 20 : 26) : (compact ? 8 : 10),
                  background: t.major ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.22)",
                  transform: "translateX(-50%)",
                }}
              />
            );
          })}
        </div>
        {/* value indicator — at center in free mode, offset by (value-anchor) when anchored */}
        <div
          style={{
            position: "absolute",
            left: indicatorX,
            top: compact ? 16 : 22,
            transform: "translateX(-50%)",
            width: 3,
            height: compact ? 30 : 40,
            background: accentColor,
            borderRadius: 2,
            boxShadow: `0 0 0 5px ${accent === "lime" ? "rgba(132,204,22,0.16)" : "rgba(59,130,246,0.16)"}`,
          }}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// WheelPicker — iOS-style 3-column DOB spinner.
// ─────────────────────────────────────────────────────────────────────────

export type WheelColumn = {
  values: string[];
  selectedIndex: number;
  onChange: (index: number) => void;
  width?: number;
};

export function WheelPicker({
  columns,
  rowHeight = 44,
}: {
  columns: WheelColumn[];
  rowHeight?: number;
}): React.ReactElement {
  const height = rowHeight * 5;
  return (
    <div style={{ position: "relative", height, background: "transparent" }}>
      {/* selection band */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: "50%",
          left: 0,
          right: 0,
          height: rowHeight,
          marginTop: -rowHeight / 2,
          borderTop: `1px solid ${T.hairlineStrong}`,
          borderBottom: `1px solid ${T.hairlineStrong}`,
          pointerEvents: "none",
        }}
      />
      {/* fade overlays */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "linear-gradient(180deg, rgba(11,15,20,0.95) 0%, rgba(11,15,20,0) 30%, rgba(11,15,20,0) 70%, rgba(11,15,20,0.95) 100%)",
        }}
      />
      <div style={{ display: "flex", height: "100%" }}>
        {columns.map((col, i) => (
          <WheelColumnUI key={i} col={col} rowHeight={rowHeight} />
        ))}
      </div>
    </div>
  );
}

function WheelColumnUI({ col, rowHeight = 44 }: { col: WheelColumn; rowHeight?: number }): React.ReactElement {
  const ref = useRef<HTMLDivElement | null>(null);
  const ROW = rowHeight;
  const dragRef = useRef<{ startY: number; startIndex: number } | null>(null);
  const wheelAccumRef = useRef(0);
  const wheelResetTimerRef = useRef<number | null>(null);

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>): void {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    dragRef.current = { startY: e.clientY, startIndex: col.selectedIndex };
  }
  function onPointerMove(e: React.PointerEvent<HTMLDivElement>): void {
    const d = dragRef.current;
    if (!d) return;
    const dy = e.clientY - d.startY;
    const next = d.startIndex - Math.round(dy / ROW);
    const clamped = Math.min(col.values.length - 1, Math.max(0, next));
    if (clamped !== col.selectedIndex) col.onChange(clamped);
  }
  function onPointerUp(e: React.PointerEvent<HTMLDivElement>): void {
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    dragRef.current = null;
  }

  // Native wheel listener via ref so we can call preventDefault (React's
  // synthetic wheel handlers are passive and can't block page scroll).
  // Accumulates deltaY and only advances the selection once per `THRESHOLD`
  // of accumulated scroll — keeps the wheel from jumping to extremes on
  // trackpad inertia.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const THRESHOLD = 40;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      if (wheelResetTimerRef.current !== null) {
        window.clearTimeout(wheelResetTimerRef.current);
      }
      wheelAccumRef.current += e.deltaY;
      let idx = col.selectedIndex;
      while (Math.abs(wheelAccumRef.current) >= THRESHOLD) {
        const dir = wheelAccumRef.current > 0 ? 1 : -1;
        const next = Math.min(col.values.length - 1, Math.max(0, idx + dir));
        if (next === idx) {
          wheelAccumRef.current = 0;
          break;
        }
        idx = next;
        wheelAccumRef.current -= dir * THRESHOLD;
      }
      if (idx !== col.selectedIndex) col.onChange(idx);
      // After ~120ms of no wheel activity, reset the accumulator so a fresh
      // gesture doesn't inherit residual delta.
      wheelResetTimerRef.current = window.setTimeout(() => {
        wheelAccumRef.current = 0;
        wheelResetTimerRef.current = null;
      }, 120);
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => {
      el.removeEventListener("wheel", handler);
      if (wheelResetTimerRef.current !== null) {
        window.clearTimeout(wheelResetTimerRef.current);
      }
    };
  }, [col]);

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        flex: 1,
        position: "relative",
        height: "100%",
        overflow: "hidden",
        cursor: "grab",
        touchAction: "none",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: `calc(50% - ${col.selectedIndex * ROW + ROW / 2}px)`,
          transition: `top ${T.motion}`,
        }}
      >
        {col.values.map((v, i) => {
          const dist = Math.abs(i - col.selectedIndex);
          const opacity = dist === 0 ? 1 : dist === 1 ? 0.5 : dist === 2 ? 0.25 : 0.1;
          const weight = dist === 0 ? 700 : 500;
          return (
            <div
              key={v + i}
              onClick={() => col.onChange(i)}
              style={{
                height: ROW,
                lineHeight: `${ROW}px`,
                textAlign: "center",
                fontSize: "1.4rem",
                fontWeight: weight,
                opacity,
                color: T.fg,
                ...T.tabular,
                letterSpacing: "-0.01em",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              {v}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PaceSlider — continuous range with optional caution band.
// ─────────────────────────────────────────────────────────────────────────

export function PaceSlider({
  value,
  min = 0,
  max = 2,
  step = 0.05,
  cautionFrom = 1.6,
  showAxisLabels = false,
  onChange,
}: {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  cautionFrom?: number;
  showAxisLabels?: boolean;
  onChange: (v: number) => void;
}): React.ReactElement {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(320);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const measure = () => setWidth(el.offsetWidth || 320);
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  function setFromX(clientX: number): void {
    const el = trackRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // The visible track is inset by 12px on each side (see background bar
    // below), so map pointer-x to that inset range — clicking at the visual
    // edges should land cleanly on ratio 0 and 1.
    const usable = Math.max(1, r.width - 24);
    const ratio = Math.min(1, Math.max(0, (clientX - r.left - 12) / usable));
    const raw = min + ratio * (max - min);
    const snapped = Math.round(raw / step) * step;
    onChange(+snapped.toFixed(2));
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>): void {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    setFromX(e.clientX);
  }
  function onPointerMove(e: React.PointerEvent<HTMLDivElement>): void {
    if (e.buttons === 0) return;
    setFromX(e.clientX);
  }

  const ratio = (value - min) / (max - min || 1);
  const cautionRatio = (cautionFrom - min) / (max - min || 1);
  const inCaution = value >= cautionFrom;

  return (
    <div style={{ userSelect: "none" }}>
      <div
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        style={{
          position: "relative",
          height: 56,
          padding: "20px 12px",
          cursor: "pointer",
          touchAction: "none",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: 12,
            right: 12,
            height: 6,
            transform: "translateY(-50%)",
            borderRadius: 999,
            background: "rgba(255,255,255,0.10)",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: 12,
            width: `calc(${Math.max(0, ratio * 100)}% - ${ratio * 24}px)`,
            height: 6,
            transform: "translateY(-50%)",
            borderRadius: 999,
            background: inCaution ? "linear-gradient(90deg, #84CC16 0%, #F59E0B 100%)" : T.ctaGrad,
          }}
        />
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: "50%",
            left: `calc(${cautionRatio * 100}% + ${(1 - 2 * cautionRatio) * 12}px)`,
            transform: "translate(-50%, -50%)",
            width: 2,
            height: 22,
            background: "rgba(239,68,68,0.6)",
            borderRadius: 1,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: `calc(${ratio * 100}% + ${(1 - 2 * ratio) * 12}px)`,
            transform: "translate(-50%, -50%)",
            width: 26,
            height: 26,
            borderRadius: 999,
            background: "#fff",
            boxShadow: "0 4px 14px rgba(0,0,0,0.6)",
          }}
        >
          <div style={{ position: "absolute", inset: 6, borderRadius: 999, background: "#0a0e14" }} />
        </div>
      </div>
      {showAxisLabels ? (
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, color: T.muted, fontSize: "0.74rem", fontWeight: 700, ...T.tabular }}>
          <span>{min.toFixed(1)} lb/wk</span>
          <span style={{ color: inCaution ? "#f87171" : T.muted }}>{max.toFixed(1)} lb/wk</span>
        </div>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SelectableCard — large tap-target row used for goal / sex / activity.
// ─────────────────────────────────────────────────────────────────────────

export function SelectableCard({
  selected,
  onClick,
  icon,
  title,
  body,
}: {
  selected: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  title: string;
  body?: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        gap: 16,
        width: "100%",
        padding: "18px 20px",
        borderRadius: radii.card,
        background: selected ? "rgba(132,204,22,0.07)" : "rgba(255,255,255,0.025)",
        border: `1.5px solid ${selected ? "rgba(132,204,22,0.55)" : T.hairline}`,
        boxShadow: selected ? "0 0 0 3px rgba(132,204,22,0.10)" : "none",
        cursor: "pointer",
        transition: `all ${T.motion}`,
        color: T.fg,
      }}
    >
      {icon ? (
        <div
          style={{
            flexShrink: 0,
            width: 44,
            height: 44,
            borderRadius: 12,
            display: "grid",
            placeItems: "center",
            background: selected ? "rgba(132,204,22,0.14)" : "rgba(255,255,255,0.04)",
            border: `1px solid ${selected ? "rgba(132,204,22,0.40)" : T.hairlineStrong}`,
            color: selected ? T.lime : T.fg2,
          }}
        >
          {icon}
        </div>
      ) : null}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "1.05rem", fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.2 }}>
          {title}
        </div>
        {body ? (
          <div style={{ color: T.fg2, fontSize: "0.85rem", lineHeight: 1.4, marginTop: 4 }}>{body}</div>
        ) : null}
      </div>
      <div
        aria-hidden
        style={{
          flexShrink: 0,
          width: 22,
          height: 22,
          borderRadius: 999,
          border: `1.5px solid ${selected ? T.lime : T.hairlineStrong}`,
          display: "grid",
          placeItems: "center",
        }}
      >
        {selected ? <div style={{ width: 10, height: 10, borderRadius: 999, background: T.lime }} /> : null}
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Chip — small pill for pace presets.
// ─────────────────────────────────────────────────────────────────────────

export function Chip({
  active,
  caution,
  onClick,
  children,
}: {
  active?: boolean;
  caution?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "10px 16px",
        borderRadius: 999,
        background: active ? T.fg : caution ? "rgba(245,158,11,0.10)" : "rgba(255,255,255,0.04)",
        color: active ? "#0a0e14" : caution ? "#fde68a" : T.fg2,
        border: `1px solid ${active ? "transparent" : caution ? "rgba(245,158,11,0.35)" : T.hairlineStrong}`,
        fontWeight: 700,
        fontSize: "0.84rem",
        cursor: "pointer",
        transition: `all ${T.motion}`,
        letterSpacing: "-0.005em",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// DateField — calendar popover (button with calendar icon + month grid).
// Used on desktop where the native <input type=date> picker is inconsistent
// across browsers and doesn't match the dark theme.
// ─────────────────────────────────────────────────────────────────────────

const DF_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DF_DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

function dfParse(ymd: string): { y: number; mo: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || "");
  if (!m) return null;
  return { y: parseInt(m[1], 10), mo: parseInt(m[2], 10) - 1, d: parseInt(m[3], 10) };
}

function dfPad(n: number): string {
  return String(n).padStart(2, "0");
}

function dfFmt(y: number, m: number, d: number): string {
  return `${y}-${dfPad(m + 1)}-${dfPad(d)}`;
}

function dfFormatLong(ymd: string): string {
  const p = dfParse(ymd);
  if (!p) return "";
  return new Date(p.y, p.mo, p.d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function dfDaysInMonth(y: number, m: number): number {
  return new Date(y, m + 1, 0).getDate();
}

export type DateFieldProps = {
  value: string; // YYYY-MM-DD
  min?: string;
  max?: string;
  onChange: (v: string) => void;
  placeholder?: string;
};

export function DateField({ value, min, max, onChange, placeholder = "Select a date" }: DateFieldProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const parsed = dfParse(value);
  const today = new Date();
  const initialView = parsed ?? { y: today.getFullYear(), mo: today.getMonth(), d: today.getDate() };
  const [view, setView] = useState({ y: initialView.y, mo: initialView.mo });

  // When the value changes externally (and the popover is closed), follow.
  useEffect(() => {
    if (!open) {
      const p = dfParse(value);
      if (p) setView({ y: p.y, mo: p.mo });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Outside click + ESC to close.
  useEffect(() => {
    if (!open) return;
    const onMouse = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouse);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouse);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const minP = min ? dfParse(min) : null;
  const maxP = max ? dfParse(max) : null;
  const minDate = minP ? new Date(minP.y, minP.mo, minP.d) : null;
  const maxDate = maxP ? new Date(maxP.y, maxP.mo, maxP.d) : null;

  // Year range for the year dropdown.
  const yearMin = minP?.y ?? today.getFullYear() - 100;
  const yearMax = maxP?.y ?? today.getFullYear();
  const yearOptions: number[] = [];
  for (let y = yearMax; y >= yearMin; y -= 1) yearOptions.push(y);

  function goPrevMonth(): void {
    setView((v) => (v.mo === 0 ? { y: v.y - 1, mo: 11 } : { y: v.y, mo: v.mo - 1 }));
  }
  function goNextMonth(): void {
    setView((v) => (v.mo === 11 ? { y: v.y + 1, mo: 0 } : { y: v.y, mo: v.mo + 1 }));
  }

  // Grid: 6 rows × 7 cols = 42 cells.
  const firstDay = new Date(view.y, view.mo, 1).getDay();
  const daysInThis = dfDaysInMonth(view.y, view.mo);
  const cells: ({ y: number; mo: number; d: number } | null)[] = [];
  for (let i = 0; i < firstDay; i += 1) cells.push(null);
  for (let d = 1; d <= daysInThis; d += 1) cells.push({ y: view.y, mo: view.mo, d });
  while (cells.length < 42) cells.push(null);

  const navBtnStyle: React.CSSProperties = {
    width: 30,
    height: 30,
    display: "grid",
    placeItems: "center",
    background: "rgba(255,255,255,0.04)",
    border: `1px solid ${T.hairlineStrong}`,
    borderRadius: 8,
    color: T.fg,
    cursor: "pointer",
    flexShrink: 0,
  };

  const selectStyle: React.CSSProperties = {
    appearance: "none",
    WebkitAppearance: "none",
    background: "rgba(255,255,255,0.04)",
    border: `1px solid ${T.hairlineStrong}`,
    borderRadius: 8,
    color: T.fg,
    fontSize: "0.86rem",
    fontWeight: 700,
    padding: "6px 28px 6px 10px",
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "-0.005em",
    backgroundImage:
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none' stroke='white' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><path d='M1 1l4 4 4-4'/></svg>\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 10px center",
    backgroundSize: "10px 6px",
  };

  return (
    <div ref={containerRef} style={{ position: "relative", maxWidth: 320, width: "100%" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "14px 16px",
          background: "rgba(255,255,255,0.04)",
          border: `1px solid ${open ? T.blue : T.hairlineStrong}`,
          borderRadius: 12,
          color: T.fg,
          fontSize: "1.0rem",
          fontWeight: 600,
          cursor: "pointer",
          fontFamily: "inherit",
          letterSpacing: "-0.005em",
          transition: `border-color ${T.motion}`,
          boxShadow: open ? T.focusRing : "none",
          ...T.tabular,
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ color: T.fg2, flexShrink: 0 }}
          aria-hidden
        >
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <span style={{ flex: 1, textAlign: "left", color: parsed ? T.fg : T.muted }}>
          {parsed ? dfFormatLong(value) : placeholder}
        </span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ color: T.muted, transform: open ? "rotate(180deg)" : "none", transition: `transform ${T.motion}` }}
          aria-hidden
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open ? (
        <div
          role="dialog"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 350,
            width: 320,
            padding: 14,
            background: T.bgCard,
            border: `1px solid ${T.hairlineStrong}`,
            borderRadius: 14,
            boxShadow: "0 12px 32px rgba(0,0,0,0.55)",
          }}
        >
          {/* Header — month + year selects with month prev/next arrows */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
            <button type="button" onClick={goPrevMonth} style={navBtnStyle} aria-label="Previous month">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <div style={{ display: "flex", gap: 6, flex: 1, justifyContent: "center" }}>
              <select
                value={view.mo}
                onChange={(e) => setView((v) => ({ ...v, mo: parseInt(e.target.value, 10) }))}
                style={selectStyle}
                aria-label="Month"
              >
                {DF_MONTHS.map((m, i) => (
                  <option key={m} value={i} style={{ background: T.bgElev, color: T.fg }}>
                    {m}
                  </option>
                ))}
              </select>
              <select
                value={view.y}
                onChange={(e) => setView((v) => ({ ...v, y: parseInt(e.target.value, 10) }))}
                style={selectStyle}
                aria-label="Year"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y} style={{ background: T.bgElev, color: T.fg }}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <button type="button" onClick={goNextMonth} style={navBtnStyle} aria-label="Next month">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>

          {/* Day-of-week labels */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4 }}>
            {DF_DAY_LABELS.map((d, i) => (
              <div
                key={i}
                style={{
                  textAlign: "center",
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: T.muted,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  padding: "4px 0",
                }}
              >
                {d}
              </div>
            ))}
          </div>

          {/* Date grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
            {cells.map((cell, i) => {
              if (!cell) return <div key={i} style={{ aspectRatio: "1 / 1" }} />;
              const ymd = dfFmt(cell.y, cell.mo, cell.d);
              const cellDate = new Date(cell.y, cell.mo, cell.d);
              const disabled = (minDate && cellDate < minDate) || (maxDate && cellDate > maxDate);
              const selected = parsed && cell.y === parsed.y && cell.mo === parsed.mo && cell.d === parsed.d;
              const isToday =
                cell.y === today.getFullYear() && cell.mo === today.getMonth() && cell.d === today.getDate();
              return (
                <button
                  key={i}
                  type="button"
                  disabled={!!disabled}
                  onClick={() => {
                    onChange(ymd);
                    setOpen(false);
                  }}
                  style={{
                    aspectRatio: "1 / 1",
                    border: "none",
                    background: selected ? T.fg : "transparent",
                    color: selected
                      ? "#0a0e14"
                      : disabled
                        ? T.disabled
                        : isToday
                          ? T.lime
                          : T.fg,
                    fontWeight: selected ? 800 : isToday ? 700 : 600,
                    fontSize: "0.86rem",
                    cursor: disabled ? "not-allowed" : "pointer",
                    borderRadius: 8,
                    transition: `background ${T.motion}`,
                    fontFamily: "inherit",
                    ...T.tabular,
                  }}
                >
                  {cell.d}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
